import { useEffect, useState } from "react";
import { ORDER_STATUS, DOWNGRADE_SKU_HIERARCHY } from "../utils/constants";

/**
 * Filters downgrade plan options based on the user's current plan price.
 *
 * Rules:
 *  - If the current plan price exists in the plan list → show only cheaper plans.
 *  - If the current plan price is NOT in the list (e.g. $29.95, $1.95) → show all plans.
 *  - If no cheaper plans remain and step 2 is active → auto-advance to step 4.
 *
 * @param {object[]} planData      - All downgrade plans, sorted ascending by unitPrice.
 * @param {object|null} billingDetails - Current user billing info ({ unitPrice, ... }).
 * @param {number} step            - Current wizard step.
 * @param {Function} setStep       - Step setter.
 * @param {boolean} loading        - True while planData is still fetching.
 * @returns {{ availablePlanData, selectedPlan, setSelectedPlan, cheapestPlan }}
 */
export function useAvailableDowngradePlans({
  planData,
  billingDetails,
  step,
  setStep,
  loading,
}) {
  const [selectedPlan, setSelectedPlan] = useState(3);

  // If the subscription is already cancelled, show all plans fresh (ignore previous plan price).
  const isCancelled = billingDetails?.orderStatus === ORDER_STATUS.CANCEL;

  // Use the skuSubCategory stored in billing details (via PushBillingDetails)
  const currentPlanSKU =
    !isCancelled && billingDetails?.skuSubCategory
      ? billingDetails.skuSubCategory
      : null;

  // Determine the current plan's position in the hierarchy
  const currentHierarchyIndex = currentPlanSKU
    ? DOWNGRADE_SKU_HIERARCHY.indexOf(currentPlanSKU)
    : -1;

  // If current plan is in the hierarchy, show only lower plans; otherwise show all
  const availablePlanData =
    !isCancelled && billingDetails?.unitPrice && currentHierarchyIndex >= 0
      ? planData.filter((plan) => {
          const planIndex = DOWNGRADE_SKU_HIERARCHY.indexOf(plan.skuSubCategory);
          return planIndex > currentHierarchyIndex;
        })
      : planData;

  // Auto-advance to step 4 when there are no cheaper plans to offer
  useEffect(() => {
    if (step === 2 && !loading && billingDetails && availablePlanData.length === 0) {
      setStep(4);
    }
  }, [step, loading, billingDetails, availablePlanData.length, setStep]);

  // Default to the "Most popular" plan (middle of the list) once plans load
  useEffect(() => {
    if (availablePlanData.length > 0) {
      setSelectedPlan(Math.floor(availablePlanData.length / 2) + 1);
    }
  }, [availablePlanData.length, setSelectedPlan]);

  const cheapestPlan = availablePlanData.length > 0 ? availablePlanData[0] : null;

  // True once billing has loaded and there are genuinely no cheaper plans to offer
  const noPlansAvailable =
    !loading && billingDetails !== null && availablePlanData.length === 0;

  return { availablePlanData, selectedPlan, setSelectedPlan, cheapestPlan, noPlansAvailable };
}
